import type { ComponentPropsWithRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';

const infoType = {
    error: {
        style: 'border-icoRed-200 bg-icoRed-100 dark:bg-icoRed/20 dark:border-icoRed text-icoRed',
        icon: 'disconnect',
        iconLoaderColor: 'text-icoRed'
    },
    success: {
        style: 'border-icoGreen-200 bg-icoGreen-100 dark:bg-icoGreen/20 dark:border-icoGreen text-icoGreen',
        icon: 'check',
        iconLoaderColor: 'text-icoGreen'
    },
    warning: {
        style: 'border-icoOrange-200 bg-icoOrange-100 dark:bg-icoOrange/20 dark:border-icoOrange text-icoOrange',
        icon: 'disconnect',
        iconLoaderColor: 'text-icoOrange'
    },
    info: {
        style: 'border-icoBlue-200 bg-icoBlue-100 dark:bg-icoBlue/20 dark:border-icoBlue text-icoBlue',
        icon: 'disconnect',
        iconLoaderColor: 'text-icoBlue'
    }
};

export type InfoBannerProps = ComponentPropsWithRef<'div'> & {
    type?: keyof typeof infoType;
    className?: string;
    title?: string;
    message?: ReactNode;
    icon?: string;
    iconLoader?: boolean;
    onClose?: () => void;
    children?: ReactNode;
};

const InfoBanner = forwardRef<HTMLDivElement, InfoBannerProps>((props, ref) => {
    const {
        type = 'error',
        className = '',
        title = '',
        message = '',
        icon,
        iconLoader = false,
        onClose,
        children,
        ...restProps
    } = props;
    const { t } = useTranslation('commons');

    return (
        <div
            role="alert"
            className={clsx(
                className,
                infoType[type]?.style ?? infoType['error'].style,
                'flex w-full items-center rounded-md border px-m py-3 font-GMedium text-xs'
            )}
            {...restProps}
            ref={ref}>
            {iconLoader ? (
                <Loader
                    className={clsx(
                        'mr-m h-5 w-5',
                        infoType[type]?.iconLoaderColor ?? infoType['error'].iconLoaderColor
                    )}
                />
            ) : (
                <Icon
                    icon={icon ? icon : infoType[type]?.icon ?? 'disconnect'}
                    className="mr-m text-xl"
                />
            )}
            <div className="flex-1">
                {title ? <p className="mb-1 font-GBold">{title}</p> : null}
                {message ? <p>{message}</p> : null}
                {children}
            </div>
            {typeof onClose === 'function' ? (
                <button
                    aria-label={t('close')}
                    type="button"
                    className="relative ml-8 h-4 w-4 overflow-hidden rounded-sm text-base focus:outline-none focus:ring-2"
                    onClick={onClose}>
                    <Icon icon="close" />
                    <p className="absolute text-transparent">{t('close')}</p>
                </button>
            ) : null}
        </div>
    );
});

InfoBanner.displayName = 'InfoBanner';

export default InfoBanner;
